"use client";

import React from "react";
import { preload } from "swr";
import useSWRImmutable from "swr/immutable";
import { IPage, IPost } from "@/types/wordpress-types";
import { Column, Grid } from "@/components/layout/Grid";
import { resizeHeight } from "@/lib/sameHeight";
import Image from "next/image";
import { ImageTitleKeywordCard } from "@/components/cards/PremadeCards";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { LoadingCards } from "@/components/skeletons/LoadingCards";

// Define the fetcher function
const fetcher = (url: string) => fetch(url).then((res) => res.json());

interface RelatedPostsProps {
  currentPost: IPage;
}

type TabsType = {
  label: string;
  type: string;
  taxonomy: string;
}[];

export function RelatedPosts({ currentPost }: RelatedPostsProps) {
  const [activeTab, setActiveTab] = React.useState<string>("related");
  const [toggleFilter, setToggleFilter] = React.useState<boolean>(false);
  const relatedPostsRef = React.useRef<HTMLDivElement>(null);

  const { isInView } = useIntersectionObserver({ ref: relatedPostsRef });
  const baseUrl = process.env.NEXT_PUBLIC_API_URL + "/wp-json/wp/v2/";

  const currentPostType = currentPost.type;
  const currentPostId = currentPost.id;

  let tabs: TabsType = React.useMemo(
    () => [
      { label: "Learning", type: "learning", taxonomy: "learning-category" },
      { label: "News", type: "news", taxonomy: "" },
      { label: "Reviews", type: "reviews", taxonomy: "review-category" },
      {
        label: "Inspiration",
        type: "inspiration",
        taxonomy: "inspiration-category",
      },
    ],
    [],
  );

  let relatedTab: TabsType = [
    {
      label: "Related",
      type: "related",
      taxonomy: tabs?.filter((tab) => tab.type === currentPostType)[0].taxonomy,
    },
  ];

  tabs = relatedTab.concat(tabs);

  // Logic to determine if query param should be setup for the related category...
  const relatedCategory =
    currentPost[relatedTab[0].taxonomy]?.length > 0
      ? `&${relatedTab[0].taxonomy}=${currentPost[relatedTab[0].taxonomy][0]} `
      : "";

  // API Urls for filtering
  const postTypeFilters: { [key: string]: string } = React.useMemo(() => {
    return {
      related: `${currentPostType}?exclude=${currentPostId}&per_page=5&orderby=date&order=desc${relatedCategory}`,
      learning: `learning?exclude=${currentPostId}&per_page=5&orderby=date&order=desc&learning-category_exclude=1951`,
      inspiration: `inspiration?exclude=${currentPostId}&per_page=5&orderby=date&order=desc`,
      news: `news?exclude=${currentPostId}&per_page=5&orderby=date&order=desc`,
      reviews: `reviews?exclude=${currentPostId}&per_page=5&orderby=date&order=desc`,
    };
  }, [currentPostId, currentPostType, relatedCategory]);

  // useEffect for prefetching page 1 of each category filter - SWR preload function
  React.useEffect(() => {
    if (isInView) {
      // Preload data for all categories in the background
      tabs.forEach((tab) => {
        let preloadUrl = baseUrl + postTypeFilters[tab.type];
        preload(preloadUrl, fetcher);
      });
    }
  }, [currentPost, postTypeFilters, isInView, tabs, baseUrl]);

  const { data: posts, isLoading } = useSWRImmutable<IPost[]>(
    baseUrl + postTypeFilters[activeTab],
    fetcher,
  );

  // Resize posts when they change...
  React.useEffect(() => {
    setTimeout(() => resizeHeight(), 150);
  }, [activeTab, posts]);

  function handleClick(tab: string) {
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  }

  return (
    <>
      {/*  Related Posts Filters  */}
      <div className="filter-posts flex justify-between items-center">
        <h3 className="font-roboto-serif font-semibold !mb-0">Recommended</h3>

        <button
          id="filter-posts"
          className={`lg:hidden w-10 h-10 flex basis-auto justify-center items-center rounded-full relative filter-posts-toggle ${toggleFilter ? "btn-primary" : "btn-tertiary"}`}
          onClick={() => setToggleFilter((prev) => !prev)}
          aria-label="Filter posts button"
        >
          {!toggleFilter ? (
            <Image
              className="select-none pointer-events-none"
              src="/icons/tune.svg"
              alt=""
              width={24}
              height={24}
            />
          ) : (
            <Image
              className="select-none pointer-events-none"
              src="/icons/tune-white.svg"
              alt=""
              width={24}
              height={24}
            />
          )}

          {toggleFilter && (
            <ul className="filter-menu absolute list-none bg-white text-left min-w-[200px] top-full right-2.5 z-[4] shadow-pn pb-1">
              <li className="text-pn-black-base px-2.5 py-1.5">Show:</li>
              {tabs.map((tab, index) => {
                return (
                  <li
                    key={tab.type}
                    className={`${activeTab === tab.type ? "active" : ""} text-pn-black-base flex items-center px-2.5 py-1.5`}
                    onClick={() => handleClick(tab.type)}
                  >
                    <span className="checkbox w-6 h-6 inline-flex justify-center items-center pr-1.5"></span>
                    {tab.label}
                  </li>
                );
              })}
            </ul>
          )}
        </button>

        <div
          role="tablist"
          aria-label="Filter Tabs"
          className="hidden lg:flex tabs gap-2.5 justify-end"
        >
          {tabs.map((tab, index) => {
            return (
              <button
                key={`tab-${tab.type}`}
                type="button"
                className={`btn rounded-btn !py-2.5 !px-5 tab ${tab.type === activeTab ? `active btn-primary bg-${tab.type}` : "btn-tertiary"}`}
                onClick={() => handleClick(tab.type)}
                tabIndex={tab.type === activeTab ? 0 : -1}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/*  Related Posts Markup  */}
      <div ref={relatedPostsRef}>
        {isLoading && (
          <LoadingCards columns={5} postCount={5} classes={"pt-10"} />
        )}

        {!isLoading && (
          <>
            {/*  Post Grid   */}
            <Grid columns={5} classes="pt-10 related-posts same-height-group">
              {(!posts || posts.length == 0) && (
                <p>Sorry, no posts available.</p>
              )}

              {/* Columns */}
              {posts &&
                posts.length > 0 &&
                posts?.map((post, index) => (
                  <Column key={`post-${index}`}>
                    <ImageTitleKeywordCard
                      classes={`max-sm:flex max-sm:justify-between max-sm:pb-5 max-sm:gap-5 max-sm:justify-start
                      max-sm:!flex-row border-bottom single-post ${post.type}`}
                      post={post}
                      ratio={"wide"}
                      metaField={"keyword"}
                    />
                  </Column>
                ))}
            </Grid>
          </>
        )}
      </div>
    </>
  );
}
